import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-react';
import useErrorState from '../../../utils/errors/useErrorState';
import { errorMessages } from '../../../utils/errors/constants';
import { formatDateToString } from '../../../utils/dateUtils/dateUtils';
import ChildQuestions from './ChildQuestions';
import PropTypes from 'prop-types';

const ChildrenChecklist = ({ rendering, fields, children, displayErrors, setCallback }) => {
  const selectedIndex = useSelector(state => state.quoteData.selectedChildIndex ?? 0);
  const [expandedItems, setExpandedItems] = useState({ [selectedIndex]: true });

  useEffect(() => {
    setExpandedItems({ [selectedIndex]: true });
  }, [selectedIndex]);

  const [error, setError] = useErrorState(rendering.uid, fields.name?.value, fields);
  const [childComplete, setChildComplete] = useState(() =>
    new Array(children.length).fill({ opened: false, errors: false })
  );
  const initialized = useRef(false);
  const dispatch = useDispatch();
  const handleToggle = (index, isExpanded) => {
    setExpandedItems(prevState => ({
      ...prevState,
      [index]: isExpanded,
    }));
  };
  const callback = () => {
    if (childComplete.some(c => c.errors)) {
      displayFirstErrorOrUnopened();
      return false;
    }
    return true;
  };
  const displayFirstErrorOrUnopened = () => {
    const firstInvalid = childComplete.findIndex(c => c.errors);
    if (firstInvalid !== -1) {
      handleToggle(firstInvalid, true);
      document
        .querySelector(`[id="${rendering.uid}_${firstInvalid}"]`)
        ?.querySelector('select, input')
        ?.focus();
    }
  };
  const updateChildState = (index, childStatus) => {
    setChildComplete(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...childStatus };
      const errorCount = updated.filter(c => c.errors).length;
      if (errorCount > 0) {
        setError(errorMessages.TOO_FEW);
      } else {
        setError(null);
      }
      return updated;
    });
  };
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
    }
  }, []);
  useEffect(() => {
    const errorCount = childComplete.filter(c => c.errors).length;
    if (error && errorCount === 0) {
      setError(null);
    }
    if (!error && errorCount > 0) {
      setError(errorMessages.TOO_FEW);
    }
  }, [childComplete, error, fields]);
  useEffect(() => {
    if (displayErrors) {
      displayFirstErrorOrUnopened();
    }
  }, [displayErrors]);
  useEffect(() => {
    if (setCallback) {
      setCallback(() => callback);
    }
    return () => setError(null);
  }, [setCallback]);
  const formatDate = dateStr => {
    if (!dateStr) {
      return '';
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return '';
    }
    return formatDateToString(date);
  };
  return (
    <div id="vds-checklist-editable" className="vds-checklist vds-checklist--edit-state">
      <div className="vds-checklist__inner">
        <div className="vds-checklist__content">
          <ul className="vds-checklist__list">
            {children.map((_child, index) => {
              const isExpanded = expandedItems[index] || false;
              const child = children[index];
              const isDobPresent = !!child?.dateOfBirth;
              const collapsedLabel = isDobPresent
                ? fields.editHeadingText?.value
                : fields.addHeadingText?.value;
              const formattedDob = formatDate(child?.dateOfBirth);
              const { title, forename, surname } = child || {};
              const hasFullName = title && forename && surname;
              const label = hasFullName
                ? `${title} ${forename} ${surname}`
                : `${fields.heading?.value} ${index + 1}`;
              return (
                <li
                  key={`${rendering.uid}_${index}`}
                  id={`${rendering.uid}_${index}`}
                  className={`vds-checklist__list-item ${
                    isExpanded ? 'vds-checklist__list-item--edit-state' : ''
                  }`}
                  aria-expanded={isExpanded}
                >
                  <div className="vds-checklist__list-item-primary" role="button">
                    <span className="vds-checklist__list-icon vds-checklist__list-icon--inactive" />
                    <span className="vds-checklist__list-icon vds-checklist__list-icon--active" />
                    <span className="vds-checklist__list-text">
                      <span key={`icon-${index}-${isExpanded}`}>
                        <i
                          className={`fa-solid fa-fw fa-${
                            isExpanded ? fields.iconOpen?.value : fields.iconClosed?.value
                          }`}
                        />
                      </span>
                      {`${label}${formattedDob ? ' – ' + formattedDob : ''}`}
                    </span>

                    <span className="vds-checklist__edit-list-item">
                      {!isExpanded && (
                        <span
                          className="vds-checklist__edit-list-text vds-checklist__edit-list-text--edit"
                          title="Add"
                          onClick={() => handleToggle(index, true)}
                        >
                          {collapsedLabel}
                        </span>
                      )}

                      {isExpanded && (
                        <span
                          className="vds-checklist__edit-list-text vds-checklist__edit-list-text--cancel"
                          title="Cancel"
                          onClick={() => handleToggle(index, false)}
                        >
                          {fields.cancelHeadingText?.value}
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="vds-checklist__editable-content" hidden={!isExpanded}>
                    <ChildQuestions
                      index={index}
                      rendering={rendering}
                      fields={fields}
                      dispatch={dispatch}
                      displayErrors={displayErrors}
                      updateChildState={updateChildState}
                      handleToggle={handleToggle}
                      childComplete={childComplete}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};
ChildrenChecklist.propTypes = {
  rendering: PropTypes.shape({
    uid: PropTypes.string.isRequired,
  }).isRequired,
  fields: PropTypes.shape({
    heading: PropTypes.shape({ value: PropTypes.string }),
    addHeadingText: PropTypes.shape({ value: PropTypes.string }),
    editHeadingText: PropTypes.shape({ value: PropTypes.string }),
    cancelHeadingText: PropTypes.shape({ value: PropTypes.string }),
    iconOpen: PropTypes.shape({ value: PropTypes.string }),
    iconClosed: PropTypes.shape({ value: PropTypes.string }),
    name: PropTypes.shape({ value: PropTypes.string }),
  }).isRequired,
  children: PropTypes.arrayOf(
    PropTypes.shape({
      dateOfBirth: PropTypes.string,
      title: PropTypes.string,
      forename: PropTypes.string,
      surname: PropTypes.string,
    })
  ).isRequired,
  displayErrors: PropTypes.bool,
  setCallback: PropTypes.func,
};

ChildrenChecklist.defaultProps = {
  displayErrors: false,
  setCallback: null,
};
const mapStateToProps = (state, props) => {
  let children = isEditorActive()
    ? [{}]
    : state.quoteData.prospectDetails.dependents?.children || [];
  return {
    ...props,
    children,
    displayErrors:
      props.displayErrors !== undefined ? props.displayErrors : state.appState?.displayErrors,
  };
};
export default connect(mapStateToProps)(ChildrenChecklist);
